/**
 * Early-Access Discount Pricing — Client-Side Percentage Calculation
 *
 * Reads regular and fixed automatic discount rules from localStorage.
 * Each rule maps a set of product IDs to a discount percentage. Calculates
 * discounted prices from the data-original-price attribute on each product
 * card element.
 * Zero API calls.
 *
 * Data flow:
 *  1. EA Discounts section stores ea_discount_rules / ea_auto_discount_rules =
 *       [{ pct: 20, codes: "CODE1", productIds: [123, 456] }, ...]
 *  2. JS builds a productId → pct lookup map
 *  3. Each price element has data-product-id — JS looks up its pct
 *  4. Discounted price = originalCents - floor(discount amount in cents)
 */

/* Fallback in case currency.js hasn't loaded yet (both are defer — order depends on DOM position) */
if (typeof window.formatMoney !== 'function') {
  window.formatMoney = function (cents, format) {
    if (typeof cents === 'string') { cents = cents.replace('.', ''); }
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision === undefined ? 2 : precision;
      thousands = thousands === undefined ? ',' : thousands;
      decimal = decimal === undefined ? '.' : decimal;
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }
    var match = formatString.match(placeholderRegex);
    if (!match) return String(formatString);
    var value = '';
    switch (match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  };
}

(function () {
  /* Shopify truncates percentage discount amounts to whole cents before
     subtracting them from the original price. */
  function calculateDiscountedCents(originalCents, discountPct) {
    return originalCents - Math.floor(originalCents * discountPct / 100);
  }

  /* ── Build product ID → discount percentage map from enabled section rules ── */
  var discountRules = [];
  try { discountRules = JSON.parse(localStorage.getItem('ea_discount_rules') || '[]'); } catch (e) {}

  /* Fixed automatic-discount rules render badges through Liquid/CSS. Include
     explicit products and variants in the same pricing map so the badge and
     price stay in sync. Tag-only rules are excluded because they can represent
     quantity tiers such as Buy More, Save More rather than one fixed price. */
  try {
    var storedAutoRules = JSON.parse(localStorage.getItem('ea_auto_discount_rules') || '[]');
    var autoDiscountRules = Array.isArray(storedAutoRules) ? storedAutoRules : (storedAutoRules.rules || []);
    autoDiscountRules.forEach(function (rule) {
      if (!rule || !rule.pct || rule.pct <= 0) return;
      var hasVariantIds = Array.isArray(rule.variantIds) && rule.variantIds.length > 0;
      var hasProductIds = Array.isArray(rule.productIds) && rule.productIds.length > 0;
      if (!hasVariantIds && (!hasProductIds || rule.productTag)) return;
      discountRules.push(rule);
    });
  } catch (e) {}

  var productPctMap = {};
  var productPdpBadgeMap = {};
  var productCartBadgeMap = {};
  var productCardBadgeMap = {};
  var productBadgeBgMap = {};
  var productBadgeTextMap = {};
  var productCardBadgeTopMap = {};
  var variantPctMap = {};
  var variantPdpBadgeMap = {};
  var variantCartBadgeMap = {};
  var variantCardBadgeMap = {};
  var variantBadgeBgMap = {};
  var variantBadgeTextMap = {};
  var variantCardBadgeTopMap = {};

  function buildMapsFromRules() {
    productPctMap = {};
    productPdpBadgeMap = {};
    productCartBadgeMap = {};
    productCardBadgeMap = {};
    productBadgeBgMap = {};
    productBadgeTextMap = {};
    productCardBadgeTopMap = {};
    variantPctMap = {};
    variantPdpBadgeMap = {};
    variantCartBadgeMap = {};
    variantCardBadgeMap = {};
    variantBadgeBgMap = {};
    variantBadgeTextMap = {};
    variantCardBadgeTopMap = {};
    discountRules.forEach(function (rule) {
      if (!rule.pct || rule.pct <= 0) return;
      var variantIds = rule.variantIds || [];
      if (variantIds.length > 0) {
        variantIds.forEach(function (vid) {
          variantPctMap[String(vid)] = rule.pct;
          variantPdpBadgeMap[String(vid)] = String(rule.pdpBadgeText ?? rule.badgeText ?? '#% Off').trim();
          variantCartBadgeMap[String(vid)] = String(rule.cartBadgeText ?? rule.badgeText ?? '#% Off').trim();
          variantCardBadgeMap[String(vid)] = String(rule.cardBadgeText ?? '#% Off').trim();
          variantBadgeBgMap[String(vid)] = rule.badgeBgColor || '#000000';
          variantBadgeTextMap[String(vid)] = rule.badgeTextColor || '#ffffff';
          variantCardBadgeTopMap[String(vid)] = rule.plpBadgeTop === true;
        });
        return;
      }
      (rule.productIds || []).forEach(function (pid) {
        productPctMap[String(pid)] = rule.pct;
        productPdpBadgeMap[String(pid)] = String(rule.pdpBadgeText ?? rule.badgeText ?? '#% Off').trim();
        productCartBadgeMap[String(pid)] = String(rule.cartBadgeText ?? rule.badgeText ?? '#% Off').trim();
        productCardBadgeMap[String(pid)] = String(rule.cardBadgeText ?? '#% Off').trim();
        productBadgeBgMap[String(pid)] = rule.badgeBgColor || '#000000';
        productBadgeTextMap[String(pid)] = rule.badgeTextColor || '#ffffff';
        productCardBadgeTopMap[String(pid)] = rule.plpBadgeTop === true;
      });
    });
  }

  function getDiscountPct(productId, variantId) {
    var variantKey = variantId == null ? '' : String(variantId);
    var productKey = productId == null ? '' : String(productId);

    if (variantKey && variantPctMap[variantKey]) return variantPctMap[variantKey];
    return productPctMap[productKey] || 0;
  }

  function getPdpQuantity() {
    var qtyInput = document.querySelector('.pdp-atc-wrap .ae--form-quantity:not(.byo-builder__bundle-qty) input[name="quantity"], .ae--form-quantity:not(.byo-builder__bundle-qty) input[name="quantity"]');
    return Math.max(parseInt(qtyInput && qtyInput.value, 10) || 1, 1);
  }

  function isBmsmPricingActive() {
    var panel = document.querySelector('[data-bmsm-panel]');
    return !!(panel && window.getComputedStyle(panel).display !== 'none');
  }

  function renderPdpSizePrice(priceEl, originalCents, currentCents, fmt) {
    var originalFormatted = window.formatMoney(originalCents, fmt);
    var currentFormatted = window.formatMoney(currentCents, fmt);

    if (currentCents < originalCents) {
      priceEl.classList.add('pdp-ss__size-price--discounted');
      priceEl.innerHTML = '<span class="pdp-ss__size-price-compare">' + originalFormatted + '</span>'
        + '<span class="pdp-ss__size-price-current">' + currentFormatted + '</span>';
    } else {
      priceEl.classList.remove('pdp-ss__size-price--discounted');
      priceEl.textContent = currentFormatted;
    }
    priceEl.dataset.eaUnitPrice = String(currentCents);
  }

  /* Keep every PDP price that represents a variant in sync with the EA rule.
     The size selector and quantity/subscription scripts render the Shopify base
     price themselves, so this is also exposed as a small bridge they can ask to
     re-run after those controls change. */
  function syncPdpSupportingPrices() {
    var fmt = window.moneyFormat || '${{amount}}';
    var options = document.querySelectorAll('.pdp-ss__size-option[data-price], .pdp-ss__dropdown-option[data-price]');

    options.forEach(function (option) {
      var priceEl = option.querySelector('.pdp-ss__size-price');
      if (!priceEl || option.dataset.available === 'false') return;

      var baseCents = parseInt(option.dataset.price, 10) || 0;
      if (!baseCents) return;

      var pct = getDiscountPct(option.dataset.productId, option.dataset.variantId);
      var displayCents = pct > 0 ? calculateDiscountedCents(baseCents, pct) : baseCents;
      renderPdpSizePrice(priceEl, baseCents, displayCents, fmt);
    });

    var activeOption = document.querySelector('.pdp-ss__dropdown-option--active[data-price], .pdp-ss__size-option--active[data-price]');
    var currentPrice = document.querySelector('.current-price-pdp');
    var productId = activeOption ? activeOption.dataset.productId : (currentPrice && currentPrice.dataset.productId);
    var variantId = activeOption ? activeOption.dataset.variantId : (currentPrice && currentPrice.dataset.variantId);
    var baseCents = activeOption
      ? (parseInt(activeOption.dataset.price, 10) || 0)
      : (parseInt(currentPrice && currentPrice.dataset.originalPrice, 10) || 0);
    var pct = getDiscountPct(productId, variantId);
    var eaCents = pct > 0 ? calculateDiscountedCents(baseCents, pct) : baseCents;

    if (!baseCents) return;

    /* The unified dropdown trigger is a copy of the active option rather than
       the option itself. Single-size products have no option panel at all, so
       fall back to the current PDP variant data for their trigger price. */
    var triggerPrice = document.querySelector('.pdp-ss__dropdown-trigger .pdp-ss__size-price');
    var triggerIsAvailable = activeOption
      ? activeOption.dataset.available !== 'false'
      : !triggerPrice || triggerPrice.textContent.trim().toLowerCase() !== 'sold out';
    if (triggerPrice && triggerIsAvailable) {
      renderPdpSizePrice(triggerPrice, baseCents, eaCents, fmt);
    }

    if (window._pdpCurrentVariant) window._pdpCurrentVariant.eaPrice = eaCents;

    /* BMSM computes a tiered bundle total in pdp-size-selector.js. It remains
       authoritative while that panel is active. */
    if (isBmsmPricingActive()) return;

    var atcPrice = document.querySelector('[data-atc-price]');
    if (!atcPrice) return;

    var rcToggle = document.getElementById('rc-toggle-btn');
    var isSubscribed = !!(rcToggle && rcToggle.getAttribute('aria-expanded') === 'true');
    var quantity = getPdpQuantity();
    var subscriptionUnitCents = Math.round(eaCents * 0.85);
    var unitCents = isSubscribed ? subscriptionUnitCents : eaCents;
    var totalCents = unitCents * quantity;
    atcPrice.textContent = window.formatMoney(totalCents, fmt);
    atcPrice.dataset.eaUnitPrice = String(unitCents);

    /* The Subscribe & Save row always previews its subscription total. When
       EA pricing applies, compare against the original variant total and apply
       the subscription savings to the EA-adjusted unit price. */
    if (pct > 0) {
      var rcCompare = document.querySelector('[data-rc-sub-compare]');
      var rcDiscount = document.querySelector('[data-rc-sub-discount]');
      if (rcCompare) rcCompare.textContent = window.formatMoney(baseCents * quantity, fmt);
      if (rcDiscount) rcDiscount.textContent = window.formatMoney(subscriptionUnitCents * quantity, fmt);
    }

    /* Reuse the ATC button's existing compare-price element so its layout and
       strikethrough match ordinary Shopify sale pricing. */
    if (pct > 0) {
      var atcCompare = document.querySelector('[data-atc-compare]');
      if (atcCompare) {
        atcCompare.textContent = window.formatMoney(baseCents * quantity, fmt);
        atcCompare.style.display = baseCents * quantity > totalCents ? 'inline' : 'none';
      }
    }
  }

  window.syncEaPdpPrices = syncPdpSupportingPrices;

  /* ── DOM updates ── */
  function updateRechargeWidgetPrices(originalCents, discountPct) {
    var fmt = window.moneyFormat || '${{amount}}';

    function doUpdate() {
      var selectors = [
        '.rc-price',
        '.rc-option__price',
        '.rc-widget__price',
        'recharge-subscription-widget [class*="price"]',
        '.rc-radio__label [class*="price"]',
        '.rc-template__radio-group [class*="price"]'
      ];
      var found = false;
      selectors.forEach(function (sel) {
        var els = document.querySelectorAll(sel);
        if (els.length > 0) {
          found = true;
          els.forEach(function (el) {
            if (el.dataset.eaRcApplied) return;
            var text = el.textContent || '';
            var match = text.match(/\$[\d,.]+/);
            if (match) {
              var rcCents = Math.round(parseFloat(match[0].replace(/[^0-9.]/g, '')) * 100);
              var newCents = calculateDiscountedCents(rcCents, discountPct);
              var newFormatted = window.formatMoney(newCents, fmt);
              el.textContent = newFormatted;
              el.dataset.eaRcApplied = '1';
            }
          });
        }
      });
      if (!found) {
        var rcWidget = document.querySelector('recharge-subscription-widget');
        if (rcWidget && rcWidget.shadowRoot) {
          selectors.forEach(function (sel) {
            var els = rcWidget.shadowRoot.querySelectorAll(sel);
            if (els.length > 0) {
              found = true;
              els.forEach(function (el) {
                if (el.dataset.eaRcApplied) return;
                var text = el.textContent || '';
                var match = text.match(/\$[\d,.]+/);
                if (match) {
                  var rcCents = Math.round(parseFloat(match[0].replace(/[^0-9.]/g, '')) * 100);
                  var newCents = calculateDiscountedCents(rcCents, discountPct);
                  var newFormatted = window.formatMoney(newCents, fmt);
                  el.textContent = newFormatted;
                  el.dataset.eaRcApplied = '1';
                }
              });
            }
          });
        }
      }
    }
    doUpdate();
    setTimeout(doUpdate, 1000);
    setTimeout(doUpdate, 2500);
    setTimeout(doUpdate, 5000);
    setTimeout(doUpdate, 8000);
  }

  function applyPriceToElement(el) {
    if (el.dataset.eaApplied) return;

    var isPdp = el.classList.contains('current-price-pdp') || !!el.closest('.price-container');

    var originalCents = parseInt(el.dataset.originalPrice, 10);
    if (!originalCents || originalCents <= 0) return;

    var productId = el.dataset.productId;
    var variantId = el.dataset.variantId;
    if (!productId && !variantId) return;

    /* Prefer variant-level targeting; fall back to product-level. */
    var discountPct, srcPdpBadge, srcCardBadge, srcBadgeBg, srcBadgeTxt, srcCardBadgeTop;
    if (variantId && variantPctMap[String(variantId)]) {
      discountPct = variantPctMap[String(variantId)];
      srcPdpBadge = variantPdpBadgeMap[String(variantId)];
      srcCardBadge = variantCardBadgeMap[String(variantId)];
      srcBadgeBg = variantBadgeBgMap[String(variantId)];
      srcBadgeTxt = variantBadgeTextMap[String(variantId)];
      srcCardBadgeTop = variantCardBadgeTopMap[String(variantId)];
    } else {
      discountPct = productPctMap[String(productId)] || 0;
      srcPdpBadge = productPdpBadgeMap[String(productId)];
      srcCardBadge = productCardBadgeMap[String(productId)];
      srcBadgeBg = productBadgeBgMap[String(productId)];
      srcBadgeTxt = productBadgeTextMap[String(productId)];
      srcCardBadgeTop = productCardBadgeTopMap[String(productId)];
    }

    var pricingCard = el.closest('.ae--product-card, .card-product');
    var variantDiscountBadge = pricingCard && pricingCard.querySelector('[data-variant-discount-badge]');

    if (!discountPct || discountPct <= 0) {
      if (variantDiscountBadge) variantDiscountBadge.style.removeProperty('display');
      if (isPdp) {
        var pdpPriceContainer = el.closest('.price-container');
        var pdpBadge = pdpPriceContainer && pdpPriceContainer.querySelector('.ea-auto-badge, .ea-discount-badge');
        if (pdpBadge) {
          pdpBadge.classList.remove('ea-discount-badge');
          pdpBadge.style.setProperty('display', 'none');
          pdpBadge.style.removeProperty('background-color');
          pdpBadge.style.removeProperty('color');
          pdpBadge.removeAttribute('name');
          var pdpBadgeText = pdpBadge.querySelector('.discount-text');
          if (pdpBadgeText) pdpBadgeText.textContent = '';
        }
      }
      /* If switching flavors, remove any existing EA badge and restore plain price */
      var card = el.closest('.ae--product-card, .card-product');
      if (card) {
        var existingBadge = card.querySelector('.ea-discount-badge--top-client');
        if (existingBadge) existingBadge.remove();
        var belowBadge = card.querySelector('.ea-auto-badge--below.ea-discount-badge-client');
        if (belowBadge) {
          belowBadge.classList.remove('ea-discount-badge-client');
          belowBadge.style.removeProperty('display');
          belowBadge.style.removeProperty('background-color');
          belowBadge.style.removeProperty('color');
          var belowText = belowBadge.querySelector('.ea-auto-badge__text');
          if (belowText) belowText.textContent = '';
        }
        /* Restore hidden sibling badges */
        var bc = card.querySelector('.badges-container');
        if (bc) {
          Array.from(bc.querySelectorAll('.badge-hidden-by-discount')).forEach(function (child) {
            child.style.display = '';
            child.classList.remove('badge-hidden-by-discount');
          });
        }
      }
      var fmt = window.moneyFormat || '${{amount}}';
      if (isPdp) {el.innerHTML = window.formatMoney(originalCents, fmt); }
      el.dataset.eaApplied = '1';
      return;
    }

    /* The EA rule owns the promotional badge for this card. The variant's
       compare-at badge represents the same offer and would duplicate it. */
    if (variantDiscountBadge) {
      if (String(srcCardBadge ?? '#% Off').trim()) variantDiscountBadge.style.setProperty('display', 'none', 'important');
      else variantDiscountBadge.style.removeProperty('display');
    }

    var discountedCents = calculateDiscountedCents(originalCents, discountPct);

    if (discountedCents >= originalCents || discountedCents <= 0) return;

    var fmt = window.moneyFormat || '${{amount}}';
    var originalFormatted = window.formatMoney(originalCents, fmt);
    var discountedFormatted = window.formatMoney(discountedCents, fmt);
    var pctOff = discountPct;
    var badgeTemplate = srcPdpBadge ?? '#% Off';
    var badgeText = badgeTemplate.replace(/#/g, String(pctOff));
    var cardBadgeTemplate = srcCardBadge ?? '#% Off';
    var cardBadgeText = cardBadgeTemplate.replace(/#/g, String(pctOff));
    var badgeBg = srcBadgeBg || '#000000';
    var badgeTxt = srcBadgeTxt || '#ffffff';
    var badgeStyle = 'background-color:' + badgeBg + ';color:' + badgeTxt + ';';

    if (!cardBadgeText.trim() && pricingCard) {
      pricingCard.querySelector('.ea-discount-badge--top-client')?.remove();
      var emptyLowerBadge = pricingCard.querySelector('.ea-auto-badge--below.ea-discount-badge-client');
      if (emptyLowerBadge) {
        emptyLowerBadge.classList.remove('ea-discount-badge-client');
        emptyLowerBadge.style.removeProperty('display');
        emptyLowerBadge.style.removeProperty('background-color');
        emptyLowerBadge.style.removeProperty('color');
      }
      pricingCard.querySelectorAll('.badge-hidden-by-discount').forEach(function (badge) {
        badge.style.removeProperty('display');
        badge.classList.remove('badge-hidden-by-discount');
      });
    }
    if (isPdp && !badgeText.trim()) {
      var emptyPdpBadge = el.closest('.price-container')?.querySelector('.ea-auto-badge, .ea-discount-badge');
      if (emptyPdpBadge) emptyPdpBadge.style.setProperty('display', 'none', 'important');
    }

    if (isPdp) {
      var container = el.closest('.comparing-prices-pdp');
      if (container) {
        var compareEl = container.querySelector('.compare-at-price-pdp');
        var breaksEl = container.querySelector('.breaks');
        if (compareEl) {
          compareEl.innerHTML = originalFormatted;
          compareEl.classList.add('sale');
        }
        if (breaksEl) breaksEl.classList.add('sale');
      }
      el.innerHTML = discountedFormatted;

      /* Add promotional badge on PDP */
      if (pctOff > 0 && badgeText.trim()) {
        var priceContainer = el.closest('.price-container');
        var automaticPdpBadge = priceContainer && priceContainer.querySelector('.ea-auto-badge');
        if (automaticPdpBadge) {
          automaticPdpBadge.classList.remove('ea-discount-badge', 'badge-hidden-by-discount');
          automaticPdpBadge.style.removeProperty('display');
          automaticPdpBadge.style.setProperty('background-color', badgeBg);
          automaticPdpBadge.style.setProperty('color', badgeTxt);
          automaticPdpBadge.removeAttribute('name');
          var automaticPdpBadgeText = automaticPdpBadge.querySelector('.discount-text');
          if (automaticPdpBadgeText) automaticPdpBadgeText.textContent = '';
        } else if (priceContainer) {
          var existingPdpBadge = priceContainer.querySelector('.ea-discount-badge, .ea-auto-badge');
          if (existingPdpBadge) {
            existingPdpBadge.classList.add('ea-discount-badge');
            existingPdpBadge.setAttribute('name', 'Sale Badge');
            existingPdpBadge.setAttribute('style', 'display:inline-block;margin:8px 0;' + badgeStyle);
            var existingPdpBadgeText = existingPdpBadge.querySelector('.discount-text');
            if (existingPdpBadgeText) existingPdpBadgeText.textContent = badgeText;
          } else {
            var badgeHtml = '<div class="promotional-badge ea-discount-badge" name="Sale Badge" style="margin:8px 0;' + badgeStyle + '">'
            + '<div class="badge-content" name="Badge Content">'
            + '<div class="discount-text" plaintext="1">' + badgeText + '</div>'
            + '</div></div>';
            priceContainer.insertAdjacentHTML('afterbegin', badgeHtml);
          }

          /* Poll for Loomi addon badge — it loads async */
          var loomiChecks = 0;
          var loomiInterval = setInterval(function () {
            var loomiAddon = document.querySelector('[id^="_loomi_addon_"] .promotional-badge');
            if (loomiAddon) {
              var loomiEl = loomiAddon.closest('[id^="_loomi_addon_"]');
              if (loomiEl) loomiEl.remove();
              clearInterval(loomiInterval);
            }
            if (++loomiChecks >= 200) clearInterval(loomiInterval); /* 200 × 100ms = 20s */
          }, 100);
          }
      }

      /* Update Recharge subscription widget prices */
      updateRechargeWidgetPrices(originalCents, discountPct);
    } else {
      /* Clear all existing children first to prevent duplicates from AE theme */
      while (el.firstChild) el.removeChild(el.firstChild);
      el.innerHTML =
        '<span class="ae--product-card-price compare-at-price" style="text-decoration:line-through;">' + originalFormatted + '</span>'
        + '&nbsp;'
        + '<span class="ae--product-card-price current-price">' + discountedFormatted + '</span>';

      if (pctOff > 0 && cardBadgeText.trim()) {
        var card = el.closest('.ae--product-card, .card-product');
        if (card) {
          var badgesContainer = card.querySelector('.badges-container');
          var belowBadge = card.querySelector('.ea-auto-badge--below');
          var suppressPlpbGwpBadge = card.classList.contains('ae--product-card--plpb')
            && String(cardBadgeText || '').trim().toLowerCase() === 'gift with purchase';

          if (suppressPlpbGwpBadge) {
            var duplicateGwpBadge = card.querySelector('.ea-discount-badge--top-client');
            if (duplicateGwpBadge) duplicateGwpBadge.remove();
            if (belowBadge) {
              belowBadge.classList.remove('ea-discount-badge-client');
              belowBadge.style.removeProperty('display');
              belowBadge.style.removeProperty('background-color');
              belowBadge.style.removeProperty('color');
            }
            el.dataset.eaApplied = '1';
            return;
          }

          if (srcCardBadgeTop || !belowBadge) {
            if (belowBadge) {
              belowBadge.classList.remove('ea-discount-badge-client');
              belowBadge.style.removeProperty('display');
              belowBadge.style.removeProperty('background-color');
              belowBadge.style.removeProperty('color');
            }
            if (badgesContainer) {
              var existingBadge = badgesContainer.querySelector('.ea-discount-badge--top-client');
              if (existingBadge) {
                /* Update existing badge text and color after a variant switch. */
                existingBadge.innerHTML = '<p class="badge badge-sale" style="' + badgeStyle + '">' + cardBadgeText + '</p>';
              } else {
                Array.from(badgesContainer.children).forEach(function (child) {
                  child.style.display = 'none';
                  child.classList.add('badge-hidden-by-discount');
                });
                var badge = document.createElement('span');
                badge.className = 'discount-badge ea-discount-badge ea-discount-badge--top-client';
                badge.innerHTML = '<p class="badge badge-sale" style="' + badgeStyle + '">' + cardBadgeText + '</p>';
                badgesContainer.appendChild(badge);
              }
            }
          } else {
            var topBadge = card.querySelector('.ea-discount-badge--top-client');
            if (topBadge) topBadge.remove();
            if (badgesContainer) {
              Array.from(badgesContainer.querySelectorAll('.badge-hidden-by-discount')).forEach(function (child) {
                child.style.display = '';
                child.classList.remove('badge-hidden-by-discount');
              });
            }
            var lowerText = belowBadge.querySelector('.ea-auto-badge__text');
            /* The EA section writes the variant-specific label with ::before.
               Keep the element itself empty so the label is not rendered twice. */
            if (lowerText) lowerText.textContent = '';
            belowBadge.classList.add('ea-discount-badge-client');
            belowBadge.style.setProperty('display', 'flex', 'important');
            if (card.classList.contains('ae--product-card--plpb')) {
              /* Percentage badges in PLPB's lower promotional row always use
                 the hard-coded discount-badge colors from the card stylesheet. */
              belowBadge.style.removeProperty('background-color');
              belowBadge.style.removeProperty('color');
            } else {
              belowBadge.style.setProperty('background-color', badgeBg, 'important');
              belowBadge.style.setProperty('color', badgeTxt, 'important');
            }
          }
        }
      }
    }
    el.dataset.eaApplied = '1';
  }

  /* ── Main ── */
  function getAllEls() {
    return Array.from(document.querySelectorAll('[data-product-id][data-original-price]:not([data-ea-applied])')).filter(function (el) {
      return !el.closest('.splide__slide--clone');
    });
  }

  /* ── Cart badges ── */
  function applyCartBadges() {
    var rows = document.querySelectorAll('.ae--cart__item[data-product-id]');
    rows.forEach(function (row) {
      var pid = String(row.dataset.productId || '');
      var vid = String(row.dataset.eaVariantId || '');
      var pctOff = variantPctMap[vid] || productPctMap[pid];
      if (!pid || !pctOff) return;
      var info = row.querySelector('.ae--cart__item_info');
      if (!info) return;
      /* The static badge is activated by the row's product/variant data.
         Only inject a fallback when that markup is unavailable. */
      if (row.querySelector('.ea-auto-badge')) return;
      /* Skip if we already injected a client-side badge */
      if (info.querySelector('.ea-discount-badge-client')) return;
      var badgeTemplate = variantCartBadgeMap[vid] ?? productCartBadgeMap[pid] ?? '#% Off';
      var badgeText = badgeTemplate.replace(/#/g, String(pctOff));
      if (!badgeText.trim()) return;
      var badgeBg = variantBadgeBgMap[vid] || productBadgeBgMap[pid] || '#000000';
      var badgeTxt = variantBadgeTextMap[vid] || productBadgeTextMap[pid] || '#ffffff';
      var badge = document.createElement('div');
      badge.className = 'promotional-badge ea-cart-badge ea-discount-badge-client';
      badge.setAttribute('name', 'Sale Badge');
      badge.setAttribute('style', 'background-color:' + badgeBg + ';color:' + badgeTxt + ';');
      badge.innerHTML = '<div class="badge-content" name="Badge Content"><div class="discount-text" plaintext="1">' + badgeText + '</div></div>';
      /* Insert after the title heading */
      var title = info.querySelector('.ae--cart__item_title');
      if (title && title.nextSibling) {
        info.insertBefore(badge, title.nextSibling);
      } else {
        info.insertBefore(badge, info.firstChild);
      }
    });
  }

  function run() {
    var els = getAllEls();
    console.log('[EA Pricing] run() found ' + els.length + ' elements');
    if (els.length === 0 && !document.querySelector('.ae--cart__item[data-product-id]')) {
      syncPdpSupportingPrices();
      return;
    }

    els.forEach(function (el) {
      applyPriceToElement(el);
    });
    applyCartBadges();
    syncPdpSupportingPrices();
  }

  /* Resolve tag-only rules to products. Explicit variant IDs remain the
     authoritative scope even when the same rule also contains a product tag. */
  function resolveTagBasedRules(rules) {
    var tagRules = rules.filter(function (r) {
      return (!r.variantIds || r.variantIds.length === 0) && r.productTag && r.productTag.trim();
    });
    if (tagRules.length === 0) return Promise.resolve();
    return Promise.all(tagRules.map(function (rule) {
      var tag = rule.productTag.trim();
      return window.fetch('/collections/all/products.json?tag=' + encodeURIComponent(tag) + '&limit=250', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          rule.productIds = (data.products || []).map(function (p) { return p.id; });
          console.log('[EA Pricing] Tag "' + tag + '" resolved to ' + rule.productIds.length + ' products');
        })
        .catch(function () {
          rule.productIds = []; /* clear on failure — never fall back to stale IDs */
          console.warn('[EA Pricing] Failed to resolve tag: ' + tag);
        });
    }));
  }

  /* ── Resolve dynamic rules, then render configured discounts ── */
  function startPricing() {
    resolveTagBasedRules(discountRules)
      .then(function () {
        buildMapsFromRules();
        if (Object.keys(productPctMap).length === 0 && Object.keys(variantPctMap).length === 0) {
          console.log('[EA Pricing] No configured products or variants matched an enabled discount rule, skipping');
          return;
        }
        console.log('[EA Pricing] ' + Object.keys(productPctMap).length + ' products with configured discounts');
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', run);
        } else {
          run();
        }
      })
      .catch(function (error) {
        console.warn('[EA Pricing] Unable to initialize discount rules', error);
      });
  }

  startPricing();

  /* Re-run when new product cards are added (load more, etc.) */
  var debounceTimer;
  var observer = new MutationObserver(function (mutations) {
    var hasNew = mutations.some(function (m) {
      for (var i = 0; i < m.addedNodes.length; i++) {
        var n = m.addedNodes[i];
        if (n.nodeType === 1 && n.querySelector && n.querySelector('[data-variant-id]:not([data-ea-applied])')) return true;
      }
      return false;
    });
    if (!hasNew) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(run, 500);
  });
  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('#load-more, [data-load-more], .load-more-btn, .view-more-btn');
    if (btn) {
      setTimeout(run, 2000);
    }
    /* Re-apply when carousel flavor buttons are clicked */
    var flavorBtn = e.target.closest('.ae--product-card-variant');
    if (flavorBtn) {
      setTimeout(run, 300);
      setTimeout(run, 800);
    }
  });

  document.addEventListener('product-card:variant-change', function () {
    setTimeout(run, 0);
  });

  /* Quantity buttons stop propagation in the PDP form. Capture the click and
     sync after their own handler has changed the input. Recharge updates its
     prices on a short timer, so its toggle gets a matching delayed sync. */
  document.addEventListener('click', function (event) {
    if (event.target.closest('.ae--form-quantity_ctrl')) {
      setTimeout(syncPdpSupportingPrices, 0);
    }
    if (event.target.closest('#rc-toggle-btn')) {
      setTimeout(syncPdpSupportingPrices, 350);
    }
  }, true);

  document.addEventListener('change', function (event) {
    if (event.target.matches('.ae--form-quantity:not(.byo-builder__bundle-qty) input[name="quantity"]')) {
      setTimeout(syncPdpSupportingPrices, 0);
    }
  });

  document.addEventListener('pdp:variant-change', function (event) {
    var detail = event.detail || {};
    var price = document.querySelector('.current-price-pdp');
    if (!price) return;

    if (detail.productId) price.dataset.productId = String(detail.productId);
    if (detail.variantId) price.dataset.variantId = String(detail.variantId);
    if (detail.price != null) price.dataset.originalPrice = String(detail.price);
    delete price.dataset.eaApplied;

    var priceContainer = price.closest('.price-container');
    if (priceContainer) {
      if (detail.variantId) priceContainer.dataset.eaVariantId = String(detail.variantId);
      if (detail.productHandle != null) priceContainer.dataset.eaProductHandle = detail.productHandle;
      if (detail.productTags != null) priceContainer.dataset.eaTags = detail.productTags;
    }

    setTimeout(run, 0);
  });

  /* ── Visually / Loomi product cards ── */
  var loomiProcessed = new WeakSet();

  function parseCents(str) {
    if (!str) return 0;
    var cleaned = str.replace(/[^0-9.]/g, '');
    return Math.round(parseFloat(cleaned) * 100);
  }

  function applyToLoomiCard(card) {
    if (loomiProcessed.has(card)) return;
    loomiProcessed.add(card);

    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return;
    var match = link.href.match(/\/products\/([^?#/]+)/);
    if (!match) return;
    var handle = match[1];

    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        var pid = String(product.id);
        var discountPct = productPctMap[pid];
        if (!discountPct || discountPct <= 0) return;

        var variant = product.variants && product.variants[0];
        if (!variant) return;
        var originalCents = variant.price;
        if (!originalCents || originalCents <= 0) return;

        var discountedCents = calculateDiscountedCents(originalCents, discountPct);
        if (discountedCents >= originalCents || discountedCents <= 0) return;

        var fmt = window.moneyFormat || '${{amount}}';
        var originalFormatted = window.formatMoney(originalCents, fmt);
        var discountedFormatted = window.formatMoney(discountedCents, fmt);
        var pctOff = discountPct;
        var cardBadgeTemplate = productCardBadgeMap[pid] ?? '#% Off';
        var cardBadgeText = cardBadgeTemplate.replace(/#/g, String(pctOff));

        /* Find and update the price element */
        var priceSelectors = [
          '.current-price',
          '.product-card-price .money',
          '.product-card-price',
          '.vsly-product-price .money',
          '.vsly-product-price',
          '[class*="price"] .money',
          '[class*="price"] span',
          '.money'
        ];
        var priceEl = null;
        for (var i = 0; i < priceSelectors.length; i++) {
          priceEl = card.querySelector(priceSelectors[i]);
          if (priceEl && priceEl.textContent.trim()) break;
          priceEl = null;
        }

        if (priceEl) {
          var comparePriceEl = card.querySelector('.compare-price');
          if (comparePriceEl) {
            comparePriceEl.innerHTML = originalFormatted;
            comparePriceEl.style.textDecoration = 'line-through';
            comparePriceEl.style.opacity = '0.3';
            comparePriceEl.style.display = '';
            comparePriceEl.style.marginRight = '8px';
          } else {
            var strikeEl = document.createElement('span');
            strikeEl.className = 'compare-price';
            strikeEl.style.textDecoration = 'line-through';
            strikeEl.style.opacity = '0.3';
            strikeEl.style.marginRight = '8px';
            strikeEl.innerHTML = originalFormatted;
            priceEl.parentElement.insertBefore(strikeEl, priceEl);
          }
          priceEl.innerHTML = discountedFormatted;
        }

        /* Add badge */
        if (cardBadgeText.trim() && !card.querySelector('.ea-discount-badge')) {
          var badgeHtml = '<div class="ea-discount-badge promotional-badge" style="position:absolute;top:8px;right:8px;z-index:5;margin:0;">'
            + '<div class="badge-content">'
            + '<div class="discount-text" plaintext="1">' + cardBadgeText + '</div>'
            + '</div></div>';
          card.style.position = card.style.position || 'relative';
          card.insertAdjacentHTML('afterbegin', badgeHtml);
        }

        console.log('[EA Pricing] Applied ' + pctOff + '% off to Loomi card: ' + handle);
      })
      .catch(function (err) {
        console.warn('[EA Pricing] Loomi card fetch error:', err);
      });
  }

  function processLoomiCards() {
    var cards = document.querySelectorAll('[id^="_loomi_addon_"] .product-card');
    cards.forEach(function (card) {
      applyToLoomiCard(card);
    });
  }

  /* Observe for Loomi cards being injected */
  var loomiDebounce;
  var loomiObserver = new MutationObserver(function (mutations) {
    var hasLoomi = mutations.some(function (m) {
      for (var i = 0; i < m.addedNodes.length; i++) {
        var n = m.addedNodes[i];
        if (n.nodeType === 1) {
          var nid = typeof n.id === 'string' ? n.id : '';
          if (nid.indexOf('_loomi_addon_') === 0) return true;
          if (n.querySelector && n.querySelector('[id^="_loomi_addon_"]')) return true;
          if (n.classList && n.classList.contains('product-card') && n.closest('[id^="_loomi_addon_"]')) return true;
        }
      }
      return false;
    });
    if (!hasLoomi) return;
    clearTimeout(loomiDebounce);
    loomiDebounce = setTimeout(processLoomiCards, 500);
  });
  loomiObserver.observe(document.body, { childList: true, subtree: true });

  /* Also run on delays for cards that may already be present */
  setTimeout(processLoomiCards, 2000);
  setTimeout(processLoomiCards, 4000);
  setTimeout(processLoomiCards, 7000);
  setTimeout(processLoomiCards, 10000);
})();
